/** 
 * @module Corrections (Plan Verification)
 * @description Component for managing and displaying correction requests related to plan verifications.
 */
import { useEffect, useMemo, useState } from "react";
import { MaterialReactTable, useMaterialReactTable, MRT_ShowHideColumnsButton } from "material-react-table";
import moment from "moment";
import { Button, Icon } from "abs-ui-components";
import "./Corrections.scss";
import PlanVerificationCorrectionsChildTable from "./CorrectionsChildTable";
import AbsModal from "../../../common/absModal/AbsModal";
import CorrectionRequestForm from "./CorrectionRequestForm";
import axiosHttp from "../../../services/interceptor";
import { emitToasterMsg } from "../../../services/toasterUtility";
import { ClaimType } from "../../../constants/claims";
import ContainedLoader from "../../../common/Loader/ContainedLoader";

/**
 * @param {object} props
 * @param {object} props.planDetails plan payload including PlanId and WcnNumber
 * @param {object} props.planVerificationFlags server-provided flags (e.g. showCreateNewCorrection)
 * @param {(claim: string) => boolean} props.userHasClaim claim checker from context
 * @param {Function} props.refreshState callback to refresh parent state after mutations
 */
const PlanVerificationCorrections = ({ planDetails, planVerificationFlags, userHasClaim, refreshState }) => {
  /* ------------------------------ State ------------------------------ */
  const [correctionData, setCorretionData] = useState([]);
  const [isCorrectionReqModalOpen, setIsCorrectionReqModalOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [selectAll, setSelectAll] = useState(false);
  const [enableResolveBtn, setEnableResolveBtn] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  /* ----------------------------- Modals ----------------------------- */
  const toggleCorrectionRequest = () => {
    setIsCorrectionReqModalOpen(!isCorrectionReqModalOpen);
  };

  /* --------------------------- Data Fetchers ------------------------ */

  const getCorrectionsData = async (planId, wcnNumber) => {
    try {
      const response = await axiosHttp.get(
        `api/PlanVerification/GetCorrections?planId=${encodeURIComponent(planId)}&wcnNumber=${encodeURIComponent(
          wcnNumber
        )}`
      );
      if (response?.data) {
        return response?.data;
      } else {
        return [];
      }
    } catch (error) {
      console.error("Error fetching corrections:", error);
      throw error;
    }
  };

  /* ----------------------------- Actions ---------------------------- */

  const resolveCorrections = async (req, wcnNumber) => {
    try {
      const response = await axiosHttp.post(
        `api/PlanVerification/ResolveCorrections?wcnNumber=${encodeURIComponent(wcnNumber)}`,
        {
          PlanId: String(req.PlanId),
          PlanCorrectionIds: (req.PlanCorrectionIds ?? []).map(String)
        }
      );
      if (response?.data) {
        emitToasterMsg("success", "Correction Request", "Request Resolved Successfully");
        setRowSelection({});
        fetchData();
        await refreshState?.();
      }
    } catch (error) {
      emitToasterMsg("error", "Correction Request", error.message);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await getCorrectionsData(planDetails?.PlanId, planDetails?.WcnNumber);
      const items = Array.isArray(res?.data) ? res.data : [];
      const normalized = items.map((x) => ({
        ...x,
        correctionHeader: x.correctionHeader ?? "",
        status: x.status ?? "",
        createdBy: x.createdBy ?? "",
        createdOn: x.createdOn ?? null
      }));
      setCorretionData(normalized);
    } catch (error) {
      console.error("Failed to fetch corrections:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (planDetails?.PlanId) {
      fetchData();
    }
  }, [planDetails?.PlanId]);

  const handleResolveCorrectionRequest = async (selectedCorrectionRequests) => {
    const ids = (selectedCorrectionRequests ?? []).map(String);
    if (ids.length > 0 && planDetails?.PlanId) {
      const payload = {
        PlanId: String(planDetails.PlanId),
        PlanCorrectionIds: ids
      };
      await resolveCorrections(payload, planDetails?.WcnNumber);
    } else {
      emitToasterMsg("error", "Correction Request", "Please select atleast one Request");
    }
  };

  useEffect(() => {
    const result =
      correctionData?.length > 0 &&
      correctionData?.some(
        (item) => Object.keys(rowSelection).includes(item?.planCorrectionId + "") && item?.status !== "Resolved"
      );
    setEnableResolveBtn(!result);
  }, [rowSelection]);

  /* ------------------------------ Columns --------------------------- */

  const correctionListColumns = useMemo(
    () => [
      {
        accessorKey: "correctionHeader",
        header: "Correction Request"
      },
      {
        accessorKey: "status",
        header: "Status"
      },
      {
        accessorKey: "createdBy",
        header: "Created By"
      },
      {
        accessorKey: "createdOn",
        header: "Created",
        accessorFn: (row) => {
          if (row.createdOn) {
            return moment.utc(row.createdOn).local().format("DD-MMM-YYYY HH:mm:ss");
          }
        }
      }
    ],
    []
  );

  /* ------------------------------ Table ----------------------------- */

  const handleSelectAll = (event, field) => {
    setSelectAll(event.target.checked);
  };

  const table = useMaterialReactTable({
    columns: correctionListColumns,
    data: correctionData,
    enableExpandAll: false,
    enableFilters: false,
    enableSorting: false,
    enableRowSelection: true,
    enableColumnPinning: false,
    enableTopToolbar:
      userHasClaim(ClaimType.ADD_CORRECTION) ||
      userHasClaim(ClaimType.DELETE_CORRECTION) ||
      userHasClaim(ClaimType.RESOLVE_CORRECTION),
    enableBottomToolbar: false,
    onRowSelectionChange: setRowSelection,
    state: { rowSelection },
    getRowId: (row) => row.planCorrectionId,
    muiDetailPanelProps: () => ({
      sx: {
        padding: 0,
        margin: 0
      }
    }),
    renderToolbarInternalActions: ({ table }) => (
      <>
        <MRT_ShowHideColumnsButton id="vessel_view_show_hide_Columns" table={table} />
      </>
    ),
    renderTopToolbarCustomActions: () => {
      return (
        <>
          <div className="container-fluid p-1">
            <div className="row p-0 m-0 d-flex align-items-center mt-2">
              {userHasClaim(ClaimType.ADD_CORRECTION) && (
                <Button
                  startIcon={<Icon icon="add" />}
                  variant="secondary"
                  className="ml-2"
                  onClick={toggleCorrectionRequest}
                  disabled={!(planVerificationFlags?.data.showCreateNewCorrection)}
                >
                  NEW CORRECTION REQUEST
                </Button>
              )}
              {userHasClaim(ClaimType.RESOLVE_CORRECTION) && (
                <Button
                  startIcon={<Icon icon="check" />}
                  onClick={() => handleResolveCorrectionRequest(Object.keys(rowSelection))}
                  variant="secondary"
                  className="ml-4"
                  disabled={enableResolveBtn}
                >
                  RESOLVE CORRECTION REQUEST
                </Button>
              )}
            </div>
          </div>
        </>
      );
    },
    muiTableHeadCellProps: ({ column }) => ({
      id: `${column.columnDef.accessorKey}`,
      sx: {
        lineHeight: "40px",
        color: "rgb(255 255 255)",
        "& .MuiButtonBase-root": {
          color: "white !important",
          "& .MuiTableSortLabel-icon": {
            color: "white !important"
          }
        }
      }
    }),
    muiTableHeadRowProps: {
      sx: {
        backgroundColor: "#042a4c !important",
        "& .MuiTableCell-head::before": {
          backgroundColor: "#042a4c !important"
        }
      }
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)"
      }
    },
    muiExpandButtonProps: ({ row, table }) => ({
      onClick: () => table.setExpanded({ [row.id]: !row.getIsExpanded() }),
      sx: {
        transform: row.getIsExpanded() ? "rotate(180deg)" : "rotate(-90deg)",
        transition: "transform 0.2s"
      }
    }),
    muiSelectAllCheckboxProps: {
      title: selectAll ? "Toggle deselect all" : "Toggle select all",
      id: "test1",
      onClick: (e, field) => handleSelectAll(e, field),
      disabled: correctionData.length === 0,
      sx: {
        color: "white"
      }
    },
    renderDetailPanel: ({ row }) => {
      return (
        <div className="mx-5">
          <PlanVerificationCorrectionsChildTable
            parentCorrectionData={row.original}
            planDetails={planDetails}
            fetchData={fetchData}
          />
        </div>
      );
    }
  });

  /* ------------------------------ Render ---------------------------- */
  
  return (
    <>
      <div className="custom-table-wrapper" style={{ position: "relative" }}>
        <MaterialReactTable table={table} />
      </div>

      {/* New Correction Request Modal */}
      <div className="corrections-request-form">
        {isCorrectionReqModalOpen && (
          <AbsModal
            isOpen={isCorrectionReqModalOpen}
            title="Correction Request"
            onClose={setIsCorrectionReqModalOpen}
            close={toggleCorrectionRequest}
          >
            <CorrectionRequestForm
              toggleCorrection={toggleCorrectionRequest}
              planDetails={planDetails}
              fetchData={fetchData}
              refreshState={refreshState}
            />
          </AbsModal>
        )}
      </div>
    </>
  );
};

export default PlanVerificationCorrections;
